import { LoginPage } from 'pages/login';
import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { ROUTES } from 'routing';
import { AuthenticationRequired } from 'routing/authentication-required';
import LazyRoute from 'shared/components/LazyRoute';
import { PrivateLazyRoute } from 'shared/components/PrivateLazyRoute';
import { useUserPermissions } from 'shared/hooks';
import { AuthLayout, PublicLayout } from 'shared/layouts';
import { NoPropsJustChildren } from 'shared/types';
import { UnauthorizedPage } from 'unauthorized/unauthorized-page';

const DemoResolver = React.lazy(() => import('../pages/demo/demoResolver'));

const DistributorManagementResolver = React.lazy(
    () => import('pages/distributorManagement/distributorManagementResolver')
);

const ApprovalsResolver = React.lazy(() => import('pages/approvals/approvalsResolver'));

const PriceHistoryResolver = React.lazy(() => import('pages/priceHistory/priceHistoryResolver'));

const ProductManagementResolver = React.lazy(() => import('pages/productManagement/ProductManagementResolver'));

const ActivePriceManagementResolver = React.lazy(
    () => import('pages/activePriceManagement/ActivePriceManagementResolver')
);

const FuturePriceManagementResolver = React.lazy(
    () => import('pages/futurePriceManagement/futurePriceManagementResolver')
);

const SettingsResolver = React.lazy(() => import('pages/settings/settingsResolver'));

function PrivateRoutes() {
    const {
        isPageProductManagementAllowed,
        isPageActivePriceManagementAllowed,
        isPageSettingsAllowed,
        isPageFuturePriceManagementAllowed
    } =
        useUserPermissions();

    return (
        <Private>
            <Switch>
                <LazyRoute path={ROUTES.demo}>
                    <DemoResolver />
                </LazyRoute>
                <LazyRoute path={ROUTES.distributorManagement}>
                    <DistributorManagementResolver />
                </LazyRoute>
                <LazyRoute path={ROUTES.approvals}>
                    <ApprovalsResolver />
                </LazyRoute>
                <LazyRoute path={ROUTES.priceHistory}>
                    <PriceHistoryResolver />
                </LazyRoute>
                <PrivateLazyRoute
                    path={ROUTES.productManagement}
                    unauthorizedRoute={ROUTES.distributorManagement}
                    isAllowed={isPageProductManagementAllowed}
                >
                    <ProductManagementResolver />
                </PrivateLazyRoute>
                <PrivateLazyRoute
                    path={ROUTES.activePriceManagement}
                    unauthorizedRoute={ROUTES.distributorManagement}
                    isAllowed={isPageActivePriceManagementAllowed}
                >
                    <ActivePriceManagementResolver />
                </PrivateLazyRoute>
                <PrivateLazyRoute
                    path={ROUTES.futurePriceManagement}
                    unauthorizedRoute={ROUTES.distributorManagement}
                    isAllowed={isPageFuturePriceManagementAllowed}
                >
                    <FuturePriceManagementResolver />
                </PrivateLazyRoute>
                <PrivateLazyRoute
                    path={ROUTES.settings}
                    unauthorizedRoute={ROUTES.distributorManagement}
                    isAllowed={isPageSettingsAllowed}
                >
                    <SettingsResolver />
                </PrivateLazyRoute>
                <Route path='*'>
                    <Redirect to={ROUTES.login} />
                </Route>
            </Switch>
        </Private>
    );
}

export function AppRoutes() {
    return (
        <Switch>
            <Route path={ROUTES.login} exact>
                <Public>
                    <LoginPage />
                </Public>
            </Route>
            <Route path={ROUTES.unauthorized} exact>
                <Public>
                    <UnauthorizedPage />
                </Public>
            </Route>
            <Route path='*'>
                <PrivateRoutes />
            </Route>
        </Switch>
    );
}

function Public({ children }: NoPropsJustChildren) {
    return <PublicLayout>{children}</PublicLayout>;
}

function Private({ children }: NoPropsJustChildren) {
    return (
        <AuthenticationRequired>
            <AuthLayout>{children}</AuthLayout>
        </AuthenticationRequired>
    );
}
